export interface RouteSeo {
  path: string;
  title: string;
  description: string;
  keywords: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
}

export function getSiteUrl(): string {
  const raw = import.meta.env.VITE_SITE_URL || '';
  return raw.replace(/\/+$/, '');
}

export const HOME_SEO: RouteSeo = {
  path: '/',
  title: 'AllChinaBuy Spreadsheet 2026 — Sellers, Finds, QC & Shipping Guides',
  description:
    'Explore the latest AllChinaBuy Spreadsheet resources, trusted sellers, product finds, QC guides, shipping costs, coupons and buying tips in one place. Independent resource for smarter product research.',
  keywords:
    'AllChinaBuy Spreadsheet, AllChinaBuy sellers, AllChinaBuy shipping, AllChinaBuy QC, AllChinaBuy coupon, AllChinaBuy buying guide, AllChinaBuy review, AllChinaBuy legit',
};

export const routeSeoMap: Record<string, RouteSeo> = {
  '/about': {
    path: '/about',
    title: 'About Us — AllChinaBuySpread Resource',
    description:
      'Learn about AllChinaBuySpread, an independent resource helping shoppers make informed AllChinaBuy decisions through honest buying guides, reviews, and research.',
    keywords: 'about AllChinaBuySpread, AllChinaBuy resource, AllChinaBuy guide, independent research',
  },
  '/safety': {
    path: '/safety',
    title: 'AllChinaBuy Safety Guide — Protect Yourself While Buying',
    description:
      'Independent AllChinaBuy safety guide covering common risks, fake websites, scams, and a practical safety checklist for safer cross-border shopping.',
    keywords: 'AllChinaBuy safety, AllChinaBuy scams, shopping agent safety, safe buying guide',
  },
  '/contact': {
    path: '/contact',
    title: 'Contact Us — AllChinaBuySpread',
    description:
      'Contact the AllChinaBuySpread team with feedback, corrections, or questions about our AllChinaBuy guides, shipping, and QC resources.',
    keywords: 'AllChinaBuy contact, AllChinaBuySpread contact, resource feedback',
  },
  '/disclaimer': {
    path: '/disclaimer',
    title: 'Disclaimer — AllChinaBuySpread',
    description:
      'Read the AllChinaBuySpread disclaimer covering independence, information accuracy, external links, and user responsibility.',
    keywords: 'AllChinaBuy disclaimer, AllChinaBuySpread disclaimer, affiliate disclosure',
  },
  '/allchinabuy-spreadsheet-complete-guide': {
    path: '/allchinabuy-spreadsheet-complete-guide',
    title: 'AllChinaBuy Spreadsheet 2026: The Complete Buyer Guide',
    description:
      'The complete AllChinaBuy Spreadsheet guide for 2026 — how it works, how to read seller data, QC photos, shipping costs, real examples, pros and cons, and FAQs.',
    keywords: 'AllChinaBuy Spreadsheet, AllChinaBuy complete guide, AllChinaBuy buyer guide, AllChinaBuy QC, AllChinaBuy shipping',
    image:
      'https://readdy.ai/api/search-image?query=A%20clean%20modern%20desk%20setup%20with%20a%20large%20monitor%20showing%20an%20organized%20product%20spreadsheet%20next%20to%20a%20pair%20of%20white%20sneakers%20a%20folded%20hoodie%20and%20a%20leather%20bag%20arranged%20on%20a%20warm%20oat%20colored%20linen%20surface%20with%20soft%20diffused%20daylight%20editorial%20ecommerce%20research%20photography%20in%20calm%20neutral%20tones&width=1200&height=600&seq=rich-guide-hero-01&orientation=landscape',
    datePublished: '2026-08-25',
    dateModified: '2026-08-25',
  },
  '/is-allchinabuy-legit': {
    path: '/is-allchinabuy-legit',
    title: 'Is AllChinaBuy Legit? Complete Safety Guide 2026',
    description:
      'Independent analysis of whether AllChinaBuy is legit, covering platform safety, payment protection, common scams, and best practices for safe buying.',
    keywords: 'is AllChinaBuy legit, AllChinaBuy safety, AllChinaBuy scam check, AllChinaBuy review',
    image:
      'https://readdy.ai/api/search-image?query=Security%20shield%20and%20verification%20badge%20concept%20on%20minimal%20white%20background%2C%20digital%20trust%20and%20safety%20theme%2C%20warm%20beige%20and%20cream%20tones%2C%20clean%20editorial%20photography%20style%2C%20soft%20natural%20lighting%2C%20professional%20aesthetic%20with%20subtle%20gold%20accents&width=1200&height=600&seq=article-legit-hero-001&orientation=landscape',
    datePublished: '2026-08-18',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-shipping-cost-guide': {
    path: '/allchinabuy-shipping-cost-guide',
    title: 'AllChinaBuy Shipping Cost Guide 2026',
    description:
      'Complete AllChinaBuy shipping cost guide with shipping methods, costs by country, and money-saving tips for international buyers in 2026.',
    keywords: 'AllChinaBuy shipping, AllChinaBuy shipping cost, AllChinaBuy delivery, international shipping',
    image:
      'https://readdy.ai/api/search-image?query=International%20shipping%20packages%20and%20delivery%20boxes%20stacked%20neatly%20on%20clean%20conveyor%20belt%2C%20logistics%20warehouse%20concept%2C%20warm%20natural%20lighting%2C%20editorial%20photography%20style%2C%20soft%20beige%20and%20cream%20tones%2C%20professional%20aesthetic%2C%20clean%20minimal%20background%20with%20subtle%20warm%20glow&width=1200&height=600&seq=article-shipping-hero-001&orientation=landscape',
    datePublished: '2026-08-15',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-coupon-guide': {
    path: '/allchinabuy-coupon-guide',
    title: 'AllChinaBuy Coupon & Discount Code Guide 2026',
    description:
      'Where to find valid AllChinaBuy coupons and discount codes, how to apply them, and strategies to maximize your savings in 2026.',
    keywords: 'AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy promo, AllChinaBuy savings',
    image:
      'https://readdy.ai/api/search-image?query=Discount%20coupon%20and%20promo%20code%20concept%20with%20minimal%20design%20elements%20on%20clean%20white%20background%2C%20percentage%20off%20badges%20and%20ticket%20stubs%2C%20warm%20beige%20and%20cream%20tones%2C%20editorial%20photography%20style%2C%20soft%20natural%20lighting%2C%20savings%20and%20shopping%20theme%2C%20professional%20aesthetic&width=1200&height=600&seq=article-coupon-hero-001&orientation=landscape',
    datePublished: '2026-08-12',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-review': {
    path: '/allchinabuy-review',
    title: 'AllChinaBuy Review: Features & Buying Experience 2026',
    description:
      'Independent AllChinaBuy review covering platform features, buying experience, and pros and cons to help you decide if it is right for you.',
    keywords: 'AllChinaBuy review, AllChinaBuy experience, AllChinaBuy features, shopping agent review',
    image:
      'https://readdy.ai/api/search-image?query=Person%20reviewing%20products%20on%20modern%20laptop%20at%20minimal%20white%20desk%2C%20coffee%20cup%20and%20notebook%20nearby%2C%20warm%20natural%20lighting%20from%20window%2C%20editorial%20photography%20style%2C%20soft%20beige%20and%20cream%20tones%2C%20product%20review%20and%20research%20concept%2C%20professional%20aesthetic&width=1200&height=600&seq=article-review-hero-001&orientation=landscape',
    datePublished: '2026-08-10',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-qc-guide': {
    path: '/allchinabuy-qc-guide',
    title: 'AllChinaBuy QC Photos Guide: What to Look For',
    description:
      'Learn how to read AllChinaBuy QC photos effectively with a category-by-category checklist and an approve vs. reject framework.',
    keywords: 'AllChinaBuy QC, AllChinaBuy QC photos, quality control guide, QC checklist',
    image:
      'https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20concept%20with%20magnifying%20glass%20examining%20product%20details%20on%20clean%20white%20surface%2C%20quality%20assurance%20theme%2C%20warm%20beige%20and%20cream%20tones%2C%20editorial%20photography%20style%2C%20soft%20natural%20lighting%2C%20professional%20aesthetic%20with%20subtle%20warm%20glow&width=1200&height=600&seq=article-qc-hero-001&orientation=landscape',
    datePublished: '2026-08-08',
    dateModified: '2026-08-25',
  },
  '/how-to-use-allchinabuy-spreadsheet': {
    path: '/how-to-use-allchinabuy-spreadsheet',
    title: 'How to Use the AllChinaBuy Spreadsheet: Step-by-Step Guide',
    description:
      'A step-by-step guide to using the AllChinaBuy Spreadsheet, from reading columns and browsing categories to checking sellers and placing your first order.',
    keywords: 'how to use AllChinaBuy Spreadsheet, AllChinaBuy tutorial, AllChinaBuy guide',
    image:
      'https://readdy.ai/api/search-image?query=Hands%20navigating%20a%20tablet%20with%20a%20sortable%20product%20list%20while%20a%20laptop%20and%20notebook%20sit%20on%20a%20minimal%20oak%20desk%20in%20soft%20morning%20light%2C%20step%20by%20step%20tutorial%20concept%2C%20warm%20neutral%20palette%20with%20clean%20workspace%20styling%2C%20editorial%20photography&width=600&height=400&seq=article-howto-2026-01&orientation=landscape',
    datePublished: '2026-08-22',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-seller-guide': {
    path: '/allchinabuy-seller-guide',
    title: 'AllChinaBuy Seller Guide: How to Find Trusted Sellers',
    description:
      'Learn how to evaluate and find trusted AllChinaBuy sellers, spot red flags, and build a reliable shortlist for safer buying.',
    keywords: 'AllChinaBuy seller guide, AllChinaBuy trusted sellers, AllChinaBuy seller review',
    image:
      'https://readdy.ai/api/search-image?query=A%20storefront%20with%20a%20seller%20rating%20display%20of%20five%20golden%20stars%20and%20a%20silhouette%20of%20a%20shopkeeper%20in%20the%20soft%20background%2C%20trusted%20seller%20concept%2C%20warm%20inviting%20retail%20lighting%2C%20minimal%20commercial%20photography&width=600&height=400&seq=article-seller-2026-01&orientation=landscape',
    datePublished: '2026-08-21',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-beginner-guide': {
    path: '/allchinabuy-beginner-guide',
    title: 'AllChinaBuy Beginner Guide: Start Buying with Confidence',
    description:
      'A complete AllChinaBuy beginner guide covering account setup, the buying flow, common mistakes, and how to build confidence.',
    keywords: 'AllChinaBuy beginner guide, AllChinaBuy first order, AllChinaBuy how to buy',
    image:
      'https://readdy.ai/api/search-image?query=A%20fresh%20startup%20flat%20lay%20with%20a%20smartphone%20shopping%20cart%20icon%20a%20coffee%20cup%20and%20colorful%20sticky%20notes%20on%20a%20clean%20white%20desk%2C%20first%20time%20buyer%20concept%2C%20bright%20optimistic%20daylight%20with%20playful%20pastel%20accents%2C%20minimal%20photography&width=600&height=400&seq=article-beginner-2026-01&orientation=landscape',
    datePublished: '2026-08-19',
    dateModified: '2026-08-25',
  },
  '/best-allchinabuy-finds': {
    path: '/best-allchinabuy-finds',
    title: 'Best AllChinaBuy Finds: Top Products Worth Buying',
    description:
      'Discover the best AllChinaBuy finds across footwear, bags, clothing, and accessories, and learn where the best value products live.',
    keywords: 'best AllChinaBuy finds, AllChinaBuy products, AllChinaBuy recommendations',
    image:
      'https://readdy.ai/api/search-image?query=A%20treasure%20hunt%20flat%20lay%20of%20trendy%20sneakers%20sunglasses%20and%20a%20cap%20arranged%20around%20a%20glowing%20spotlight%20on%20a%20dark%20slate%20surface%2C%20best%20finds%20discovery%20concept%2C%20dramatic%20focused%20lighting%20with%20vibrant%20product%20colors%2C%20minimal%20photography&width=600&height=400&seq=article-finds-2026-01&orientation=landscape',
    datePublished: '2026-08-17',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-warehouse-guide': {
    path: '/allchinabuy-warehouse-guide',
    title: 'AllChinaBuy Warehouse Guide: Storage, QC & Consolidation',
    description:
      'Understand the AllChinaBuy warehouse, including storage, free storage periods, QC photos, and order consolidation.',
    keywords: 'AllChinaBuy warehouse, AllChinaBuy storage, AllChinaBuy consolidation, AllChinaBuy QC',
    image:
      'https://readdy.ai/api/search-image?query=Rows%20of%20labeled%20cardboard%20boxes%20on%20steel%20warehouse%20shelving%20with%20a%20worker%20scanning%20a%20barcode%20in%20the%20distance%2C%20storage%20and%20fulfillment%20concept%2C%20cool%20industrial%20lighting%20with%20clean%20organized%20composition%2C%20minimal%20photography&width=600&height=400&seq=article-warehouse-2026-01&orientation=landscape',
    datePublished: '2026-08-16',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-alternatives': {
    path: '/allchinabuy-alternatives',
    title: 'AllChinaBuy Alternatives: Shopping Agent Platforms Compared',
    description:
      'Compare AllChinaBuy alternatives and shopping agent platforms on product search, QC, shipping, pricing, and consolidation.',
    keywords: 'AllChinaBuy alternatives, AllChinaBuy vs, shopping agent platforms, AllChinaBuy comparison',
    image:
      'https://readdy.ai/api/search-image?query=Two%20smartphones%20side%20by%20side%20displaying%20different%20shopping%20app%20interfaces%20on%20a%20split%20tone%20background%20of%20teal%20and%20coral%2C%20platform%20comparison%20concept%2C%20modern%20clean%20daylight%20with%20balanced%20composition%2C%20minimal%20photography&width=600&height=400&seq=article-alt-2026-01&orientation=landscape',
    datePublished: '2026-08-14',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-payment-refund-guide': {
    path: '/allchinabuy-payment-refund-guide',
    title: 'AllChinaBuy Payment & Refund Guide: What You Need to Know',
    description:
      'Everything you need to know about AllChinaBuy payments and refunds, including methods, order charges, and how to claim a refund.',
    keywords: 'AllChinaBuy payment, AllChinaBuy refund, AllChinaBuy payment methods',
    image:
      'https://readdy.ai/api/search-image?query=A%20credit%20card%20and%20smartphone%20payment%20screen%20with%20a%20small%20refund%20arrow%20icon%20on%20a%20minimal%20white%20surface%2C%20secure%20payment%20and%20refund%20concept%2C%20soft%20clean%20lighting%20with%20cool%20green%20accent%20tone%2C%20minimal%20financial%20photography&width=600&height=400&seq=article-payment-2026-01&orientation=landscape',
    datePublished: '2026-08-13',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-tracking-guide': {
    path: '/allchinabuy-tracking-guide',
    title: 'AllChinaBuy Tracking Guide: Follow Your Order Every Step',
    description:
      'Follow your AllChinaBuy order every step with this tracking guide covering tracking statuses, delays, and arrival checks.',
    keywords: 'AllChinaBuy tracking, AllChinaBuy order tracking, AllChinaBuy delivery status',
    image:
      'https://readdy.ai/api/search-image?query=A%20delivery%20package%20with%20a%20tracking%20timeline%20of%20location%20pins%20on%20a%20smartphone%20screen%20over%20a%20blurred%20city%20map%2C%20order%20tracking%20concept%2C%20cool%20blue%20tones%20with%20clean%20digital%20styling%2C%20minimal%20photography&width=600&height=400&seq=article-tracking-2026-01&orientation=landscape',
    datePublished: '2026-08-11',
    dateModified: '2026-08-25',
  },
  '/allchinabuy-qc-checklist': {
    path: '/allchinabuy-qc-checklist',
    title: 'AllChinaBuy QC Checklist: What to Verify Before Shipping',
    description:
      'A complete AllChinaBuy QC checklist for shoes, clothing, and bags, plus guidance on when to approve or reject an item.',
    keywords: 'AllChinaBuy QC checklist, AllChinaBuy quality check, AllChinaBuy inspection',
    image:
      'https://readdy.ai/api/search-image?query=A%20paper%20checklist%20with%20green%20checkmarks%20and%20a%20pen%20beside%20a%20folded%20garment%20under%20a%20bright%20desk%20lamp%2C%20quality%20checklist%20concept%2C%20crisp%20clean%20lighting%20with%20neutral%20and%20green%20tones%2C%20minimal%20photography&width=600&height=400&seq=article-checklist-2026-01&orientation=landscape',
    datePublished: '2026-08-09',
    dateModified: '2026-08-25',
  },
  '/best-allchinabuy-sellers': {
    path: '/best-allchinabuy-sellers',
    title: 'Best AllChinaBuy Sellers 2026: Trusted Shops by Category',
    description:
      'Find the best AllChinaBuy sellers of 2026 across popular categories and learn how to verify them yourself before buying.',
    keywords: 'best AllChinaBuy sellers, AllChinaBuy trusted shops, AllChinaBuy top sellers',
    image:
      'https://readdy.ai/api/search-image?query=A%20podium%20of%20three%20golden%20trophy%20cups%20with%20a%20top%20seller%20badge%20floating%20over%20a%20blurred%20marketplace%20stall%2C%20top%20sellers%20award%20concept%2C%20warm%20celebratory%20lighting%20with%20rich%20golden%20tones%2C%20minimal%20photography&width=600&height=400&seq=article-bestsellers-2026-01&orientation=landscape',
    datePublished: '2026-08-07',
    dateModified: '2026-08-25',
  },
};

export function getSeoForPath(pathname: string): RouteSeo | undefined {
  return routeSeoMap[pathname];
}